import React from 'react'

export default function QualityMetricsCard({ data, loading }) {
  if (loading) {
    return (
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
            Quality Metrics
          </h2>
          <div className="flex justify-center py-4">
            <span className="loading loading-spinner loading-md"></span>
          </div>
        </div>
      </div>
    )
  }

  if (!data) return null

  // Helper function to get metric status and color
  const getMetricStatus = (metric, value, target) => {
    if (!value && value !== 0) return { status: 'unknown', color: 'text-base-content/50', badge: 'badge-ghost' }
    
    let status = 'excellent'
    let threshold = target * 0.8 // Warning threshold at 80% of target
    
    if (metric === 'factual_consistency') {
      // Higher is better (≥ 0.95 target)
      if (value >= target) status = 'excellent'
      else if (value >= threshold) status = 'good'
      else status = 'needs_attention'
    } else if (metric === 'retrieval_similarity') {
      // Higher is better (≥ 0.35 target)
      if (value >= target) status = 'excellent'
      else if (value >= threshold) status = 'good'
      else status = 'needs_attention'
    } else if (metric === 'safety_violations') {
      // Lower is better (≤ 0.005 target for major violations)
      if (value <= target) status = 'excellent'
      else if (value <= target * 2) status = 'good'
      else status = 'needs_attention'
    } else if (metric === 'guideline_adherence') {
      // Higher is better (≥ 4.5 target)
      if (value >= target) status = 'excellent'
      else if (value >= threshold) status = 'good'
      else status = 'needs_attention'
    } else if (metric === 'communication_quality') {
      // Higher is better (≥ 4.0 target)
      if (value >= target) status = 'excellent'
      else if (value >= threshold) status = 'good'
      else status = 'needs_attention'
    }

    const colors = {
      excellent: { color: 'text-success', badge: 'badge-success' },
      good: { color: 'text-warning', badge: 'badge-warning' },
      needs_attention: { color: 'text-error', badge: 'badge-error' },
      unknown: { color: 'text-base-content/50', badge: 'badge-ghost' }
    }

    return colors[status]
  }

  const metrics = [
    {
      key: 'factual_consistency',
      label: 'Factual Consistency',
      value: data.factual_consistency,
      target: 0.95,
      format: 'percentage',
      description: 'Share of claims supported by citations',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      key: 'retrieval_similarity',
      label: 'Retrieval Quality',
      value: data.retrieval_similarity,
      target: 0.35,
      format: 'decimal',
      description: 'Cosine similarity of retrieved content',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
      )
    },
    {
      key: 'safety_violations',
      label: 'Safety Score',
      value: data.safety_violations,
      target: 0.005,
      format: 'percentage',
      description: 'Major safety violations per 1000 responses',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-2.25-3L21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      key: 'guideline_adherence',
      label: 'Guideline Adherence',
      value: data.guideline_adherence,
      target: 90,
      format: 'percentage_direct',
      description: 'Alignment with dementia-care guidelines',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      )
    },
    {
      key: 'communication_quality',
      label: 'Communication Quality',
      value: data.communication_quality,
      target: 80,
      format: 'percentage_direct',
      description: 'Empathy, readability, and helpfulness',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
        </svg>
      )
    }
  ]

  const formatValue = (value, format) => {
    if (!value && value !== 0) return 'N/A'

    switch (format) {
      case 'percentage':
        return `${(value * 100).toFixed(1)}%`
      case 'percentage_direct':
        return `${value.toFixed(1)}%`
      case 'decimal':
        return value.toFixed(3)
      case 'rating':
        return `${value.toFixed(1)}/5`
      default:
        return value.toString()
    }
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <h2 className="card-title">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
          </svg>
          Quality Metrics
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {metrics.map((metric) => {
            const status = getMetricStatus(metric.key, metric.value, metric.target)
            
            return (
              <div key={metric.key} className="bg-base-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={status.color}>
                      {metric.icon}
                    </span>
                    <span className="font-medium text-sm">{metric.label}</span>
                  </div>
                  <div className={`badge ${status.badge} badge-sm`}>
                    {formatValue(metric.value, metric.format)}
                  </div>
                </div>
                
                <div className="text-xs text-base-content/60 mb-2">
                  {metric.description}
                </div>
                
                {/* Progress bar */}
                {metric.value !== null && metric.value !== undefined && (
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        status.badge === 'badge-success' ? 'bg-success' :
                        status.badge === 'badge-warning' ? 'bg-warning' :
                        status.badge === 'badge-error' ? 'bg-error' : 'bg-base-content/20'
                      }`}
                      style={{
                        width: metric.key === 'safety_violations' 
                          ? `${Math.max(0, Math.min(100, (1 - (metric.value / (metric.target * 3))) * 100))}%`
                          : `${Math.max(0, Math.min(100, (metric.value / metric.target) * 100))}%`
                      }}
                    ></div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        
        {data.computed_at && (
          <div className="text-xs text-base-content/60 mt-4 text-center">
            Metrics computed at: {new Date(data.computed_at).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  )
}